import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { ChatMessageDto } from "@/apis/types/SuperChatLogTypes";

interface ChatMessagesTabProps {
  messages: ChatMessageDto[];
}

/**
 * 채팅 메시지 탭 컴포넌트
 *
 * @description 채팅 메시지를 채팅 형태로 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const ChatMessagesTab: React.FC<ChatMessagesTabProps> = ({
  messages,
}) => {
  const [promptLogDialog, setPromptLogDialog] = useState<{
    open: boolean;
    data: any;
  }>({ open: false, data: null });

  // 디버깅: fullPromptLog 데이터 확인
  React.useEffect(() => {
    console.log("[DEBUG] ChatMessagesTab - messages:", messages);
    messages.forEach((message, index) => {
      console.log(`[DEBUG] Message ${index}:`, {
        messageId: message.messageId,
        messageType: message.messageType,
        hasFullPromptLog: !!message.fullPromptLog,
        fullPromptLogKeys: message.fullPromptLog
          ? Object.keys(message.fullPromptLog)
          : null,
        fullPromptLog: message.fullPromptLog,
      });
    });
  }, [messages]);

  const handleOpenPromptLog = (promptLog: any) => {
    setPromptLogDialog({ open: true, data: promptLog });
  };

  const handleClosePromptLog = () => {
    setPromptLogDialog({ open: false, data: null });
  };
  const getMessageTypeLabel = (type: string) => {
    switch (type) {
      case "USER":
        return "사용자";
      case "BOT":
        return "AI";
      case "SYSTEM":
        return "시스템";
      default:
        return type;
    }
  };

  const getMessageBackgroundColor = (type: string) => {
    switch (type) {
      case "USER":
        return "primary.main";
      case "BOT":
        return "grey.100";
      case "SYSTEM":
        return "warning.light";
      default:
        return "grey.100";
    }
  };

  const getMessageTextColor = (type: string) => {
    return type === "USER" ? "white" : "text.primary";
  };

  // fullPromptLog가 있는 메시지 개수 계산
  const messagesWithPromptLog = messages.filter((msg) => msg.fullPromptLog);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        채팅 메시지 ({messages.length}개)
        {messagesWithPromptLog.length > 0 && (
          <Chip
            label={`프롬프트 로그: ${messagesWithPromptLog.length}개`}
            size="small"
            color="info"
            sx={{ ml: 1 }}
          />
        )}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {messages.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            backgroundColor: "grey.50",
            borderRadius: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            메시지가 없습니다.
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            maxHeight: "600px",
            overflowY: "auto",
            p: 1,
            backgroundColor: "grey.50",
            borderRadius: 2,
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.messageId}
              sx={{
                display: "flex",
                justifyContent:
                  message.messageType === "USER" ? "flex-end" : "flex-start",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  maxWidth: "70%",
                  minWidth: "200px",
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: getMessageBackgroundColor(
                    message.messageType
                  ),
                  color: getMessageTextColor(message.messageType),
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {/* 메시지 헤더 */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    gap: 1,
                  }}
                >
                  <Chip
                    label={getMessageTypeLabel(message.messageType)}
                    size="small"
                    variant={
                      message.messageType === "USER" ? "filled" : "outlined"
                    }
                    sx={{
                      backgroundColor:
                        message.messageType === "USER"
                          ? "rgba(255,255,255,0.2)"
                          : undefined,
                      color:
                        message.messageType === "USER" ? "white" : undefined,
                      borderColor:
                        message.messageType === "USER"
                          ? "rgba(255,255,255,0.5)"
                          : undefined,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        message.messageType === "USER"
                          ? "rgba(255,255,255,0.8)"
                          : "text.secondary",
                      fontSize: "0.75rem",
                    }}
                  >
                    {new Date(message.regDttm).toLocaleString("ko-KR")}
                  </Typography>
                </Box>

                {/* 메시지 내용 */}
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    lineHeight: 1.5,
                  }}
                >
                  {message.messageContent}
                </Typography>

                {/* 추가 정보 (AI 메시지인 경우) */}
                {message.messageType === "BOT" && (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 1,
                      borderTop: "1px solid rgba(0,0,0,0.1)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      {message.aiModelUsed && (
                        <Chip
                          label={`모델: ${message.aiModelUsed}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      )}
                      {message.processingTimeMs && (
                        <Chip
                          label={`${message.processingTimeMs}ms`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      )}
                      {message.tokensUsed && (
                        <Chip
                          label={`${message.tokensUsed} tokens`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      )}
                      {message.fullPromptLog && (
                        <Button
                          size="small"
                          startIcon={<CodeIcon />}
                          onClick={() =>
                            handleOpenPromptLog(message.fullPromptLog)
                          }
                          sx={{
                            fontSize: "0.7rem",
                            height: 24,
                            minWidth: "auto",
                            px: 1,
                          }}
                        >
                          프롬프트 로그
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* 프롬프트 로그 다이얼로그 */}
      <Dialog
        open={promptLogDialog.open}
        onClose={handleClosePromptLog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { maxHeight: "80vh" },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CodeIcon />
          전체 프롬프트 로그
        </DialogTitle>
        <DialogContent dividers>
          {promptLogDialog.data && (
            <Box sx={{ fontFamily: "monospace" }}>
              {/* AI 모델 설정 */}
              {promptLogDialog.data.aiModelConfig && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      AI 모델 설정
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: 2, backgroundColor: "grey.50" }}>
                      <pre style={{ margin: 0, fontSize: "0.85rem" }}>
                        {JSON.stringify(
                          promptLogDialog.data.aiModelConfig,
                          null,
                          2
                        )}
                      </pre>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* 세션 정보 */}
              {promptLogDialog.data.sessionInfo && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      세션 정보
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: 2, backgroundColor: "grey.50" }}>
                      <pre style={{ margin: 0, fontSize: "0.85rem" }}>
                        {JSON.stringify(
                          promptLogDialog.data.sessionInfo,
                          null,
                          2
                        )}
                      </pre>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* 시스템 프롬프트 */}
              {promptLogDialog.data.systemPrompt && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      시스템 프롬프트
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: 2, backgroundColor: "grey.50" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-wrap",
                          fontFamily: "monospace",
                          fontSize: "0.85rem",
                        }}
                      >
                        {promptLogDialog.data.systemPrompt}
                      </Typography>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* 관련 컨텐츠 */}
              {promptLogDialog.data.relatedContent && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      관련 컨텐츠
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: 2, backgroundColor: "grey.50" }}>
                      <pre style={{ margin: 0, fontSize: "0.85rem" }}>
                        {JSON.stringify(
                          promptLogDialog.data.relatedContent,
                          null,
                          2
                        )}
                      </pre>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* 전체 메시지 배열 */}
              {promptLogDialog.data.messages && (
                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      전체 메시지 배열 ({promptLogDialog.data.messages.length}
                      개)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Paper sx={{ p: 2, backgroundColor: "grey.50" }}>
                      <pre
                        style={{
                          margin: 0,
                          fontSize: "0.85rem",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          maxWidth: "100%",
                          lineHeight: 1.4,
                        }}
                      >
                        {JSON.stringify(promptLogDialog.data.messages, null, 2)}
                      </pre>
                    </Paper>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* 타임스탬프 */}
              {promptLogDialog.data.timestamp && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1,
                    backgroundColor: "info.light",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" color="info.contrastText">
                    생성 시간:{" "}
                    {new Date(promptLogDialog.data.timestamp).toLocaleString(
                      "ko-KR"
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePromptLog} variant="outlined">
            닫기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
